import cx from 'classnames'
import { bin } from 'd3-array'
import { max, size } from 'lodash/fp'
import Histogram from '~/components/Histogram'
import { CONFLUENCY_COLORS } from '../constants'
import { CELL_CONCENTRATION_BIN_EDGES } from '../utils/cellConcentrationColors'

function CellConcentrationHistogram(props: {
  cellConcentrationValues: number[]
  label?: string
  className?: string
}) {
  const { cellConcentrationValues, className, label } = props

  const marginLeft = 40 // Accommodates Y axis labels
  const marginBottom = 20 // Accommodates X axis labels

  // Cell concentration range: 1B (1e9) should be at 80th percentile
  // So xMax = 1e9 / 0.8 = 1.25e9
  const xMax = Math.max(1.25e9, ...cellConcentrationValues)

  // Calculate yMax based on actual bin distribution
  let yMax = 2
  if (size(cellConcentrationValues) > 0) {
    // Create bins to find the maximum bin count
    const histogram = bin().domain([0, xMax]).thresholds(50) // Match the histogram bins option
    const bins = histogram(cellConcentrationValues)
    const maxBinCount: number = max(bins.map(b => b.length)) || 0

    // Set yMax to be slightly above the tallest bin (with 20% buffer)
    yMax = Math.ceil(maxBinCount * 1.2)

    // Ensure a minimum yMax for visual clarity
    if (yMax < 2) yMax = 2
  }

  return (
    <div className={cx('h-[110px] w-[320px]', className)}>
      {size(cellConcentrationValues) > 0 ? (
        <Histogram
          className='h-[80px] w-[320px]'
          data={cellConcentrationValues}
          layoutOptions={{
            marginLeft,
            marginRight: 1,
            marginBottom,
            marginTop: 5, // Accommodates top Y axis label text
          }}
          axisOptions={{
            showXAxis: true,
            xTicks: 5,
            xTickFormat: (n: number) => {
              if (n === 0) return '0'
              if (n >= 1e9) {
                const value = n / 1e9
                return `${value}B`
              }
              if (n >= 1e6) {
                const value = n / 1e6
                return `${value}M`
              }
              return n.toString()
            },
            xTickLabelClassName: 'stroke-grey-dark',
            yTicks: 2,
            yTickFormat: (n: number) => (n > 0 ? `${n}` : ''),
            yAxisClassName: 'stroke-grey-dark',
            yLabel: '# wells',
            yLabelBuffer: marginLeft - 10,
            yLabelClassName: 'text-xs font-semibold stroke-grey-dark',
            yTickLineClassName: (n: number) => (n === 0 ? 'hidden' : ''),
          }}
          options={{
            xDomain: [0, xMax],
            yDomain: [0, yMax],
            bins: 50,
            hoverBins: 50,
            getBarColor: (x0: number, x1: number) => {
              const midpoint = (x0 + x1) / 2

              // Determine bin index (0-4) based on midpoint (5 bins total)
              let idx = 0
              if (midpoint < CELL_CONCENTRATION_BIN_EDGES[0]) {
                idx = 0
              } else if (
                midpoint >=
                CELL_CONCENTRATION_BIN_EDGES[CELL_CONCENTRATION_BIN_EDGES.length - 1]
              ) {
                idx = CELL_CONCENTRATION_BIN_EDGES.length // 4
              } else {
                for (let i = 0; i < CELL_CONCENTRATION_BIN_EDGES.length - 1; i++) {
                  const lo = CELL_CONCENTRATION_BIN_EDGES[i]
                  const hi = CELL_CONCENTRATION_BIN_EDGES[i + 1]
                  if (midpoint >= lo && midpoint < hi) {
                    idx = i + 1
                    break
                  }
                }
              }

              // Direct 1:1 mapping of 5 bins to 5 colors (same logic as getCellConcentrationColor)
              return CONFLUENCY_COLORS[idx]
            },
          }}
        />
      ) : (
        <div className='h-[80px] w-[320px]' />
      )}

      <div className='text-xs font-semibold text-grey-dark mt-2.5 text-center ml-10'>
        {label || 'Cell Concentration (cells/mL)'}
      </div>
    </div>
  )
}

export default CellConcentrationHistogram
